import { Component, AfterViewInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { User } from '../../shared/user.model'
import { UserService } from '../../shared/user.service';
import { SocialService } from '../../shared/social.service'
import { ApiService } from '../../shared/api.service';
import { Assignment } from '../../assignments/assignment.model';
import { PropertiesComponent } from '../../fields/properties.component';


@Component({
  selector: 'profiles',
  templateUrl: './profiles.component.html',
  styleUrls: ['./profiles.component.css']
})
export class ProfilesComponent implements AfterViewInit {

  users: User[] = []
  activeUser: User = null
  //  profiles uses keys like swarm_twitter stripped to twitter for clarity
  socialMeta = {}
  socialMetaMap = {} // required property data for displaying site-specific bios
  canEdit: boolean = false
  editMode: boolean = false // toggle on/off controls, hidden inputs
  modifiedAnItem: boolean = false
  siteSpecificBioFormat:RegExp = /bio_\d{0,3}$/ //format for finding socialMeta keys for site bios

  // required for using properties component:
  assignment:Assignment = new Assignment(0) // prevent error when selecting Past Factory
  @ViewChild(PropertiesComponent) propertiesChild:PropertiesComponent

  constructor(
    private socialService:SocialService,
    private route: ActivatedRoute,
    private userService: UserService,
    private apiService: ApiService
  ) { }

  /**
   * Determine if the active user can modify this profile
   * @returns a boolean stating whether they can or cannot
   */
  userCanEdit(): boolean {
    //  apiService.user.roles and id
    const user = this.apiService.getProp('user')
    const roles = user?.roles
    if (
        roles && (
            roles.includes('administrator')
            || roles.includes('editor')
            || this.apiService.currentUserId === this.activeUser.id
        )
    ) return true
    return false
  }

  /**
   * Set the activeUser for the profile; the person who this profile is about
   * @param idParam the id of the user to search for
   */
  setActiveUser(idParam: number): void {
    const found = this.users.find((i) => i.id === idParam)
    if (found) this.activeUser = found
  }

  /**
   * Attempt to set this.users; the list may not be loaded at time of running
   */
  tryToSetUserList(): void {
    const list:User[] = this.userService.getUsers()
    if (list.length > 0) this.users = [...list]
  }

  /**
   * Set this.socialMeta
   * If user or user 'meta' not available, resort to defaults
   * @param user the user to set socialMeta based off of
   */
  setUserSocialMeta(user:User): void {
    if (!user || !('social' in user)) {
      //  apply defaults
      this.socialMeta = {...this.socialService.getDefaultSocialMeta()}
      return
    }
    this.socialMeta = {
      ...this.socialService.getDefaultSocialMeta(),
      ...this.socialService.stripPrefixObject(user.social),
    }
  }

  /**
   * Set this.socialMetaMap, which is used to help couple socialMeta keys bio_xx with a Site
   * @param siteList the list of sites, acquired from the properties child component
   */
  setSocialMetaMap(siteList) {
      const socialMetaKeys = Object.keys(this.socialMeta)
      socialMetaKeys
      .filter(keys => this.siteSpecificBioFormat.test(keys))
      .forEach(siteBiographyKey => {
            const derivedId = siteBiographyKey.replace('bio_', '')
            const found = siteList.find(i => i.blog_id == derivedId)
            if (found) this.socialMetaMap[siteBiographyKey] = found
      })
  }

  /**
   * Turn parts of this.socialMeta into a key array for displaying in the template
   * @param goal determines rules for what will/won't be in the final key array
   * @returns an array of keys for template use
   */
  socialMetaToArray(goal:string): {} {
    // turn object into key array for use with ngFor
    const metaList = this.socialMeta
    if (goal === 'socialMedia') { // get social media link keys (ex: twitter)
        const excluded = /bio_?\d{0,3}$/ //keys to exclude bio, bio_xx, bio_x
        const criteriaEditMode = (item) => excluded.test(item) === false
        const criteriaDisplayMode = (item) => excluded.test(item) === false && this.socialMeta[item]
        if (this.editMode) return Object.keys(metaList)
                                    .filter(criteriaEditMode)
        return Object.keys(metaList)
                      .filter(criteriaDisplayMode)
    }
    if (goal === 'siteBio') {
        const included = /bio_?\d{0,3}$/ //keys like bio, bio_xx, bio_x
        const criteriaEditMode = (item) => included.test(item) === true && (item === 'bio' || item in this.socialMetaMap)
        const criteriaDisplayMode = (item) => included.test(item) === true && this.socialMeta[item]
        if (this.editMode) return Object.keys(metaList)
                                    .filter(criteriaEditMode)
        return Object.keys(metaList)
                      .filter(criteriaDisplayMode)
    }
  }

  /**
   * Extract a property name from the socialMetaMap
   * @param bioKey the index
   * @returns a string representing the name of the Site
   */
  extractPropertyName(bioKey) {
    const propertyInfo = this.socialMetaMap[bioKey]
    if (propertyInfo && 'domain' in propertyInfo) {
        const domainSplit = this.socialMetaMap[bioKey].domain.split('.')
        // possible formats: www.xx.com xx.com xx.yy.com xx.yy.zz.com where xx is the name
        return domainSplit[0] === 'www' && domainSplit[1] ? domainSplit[1] : domainSplit[0]
    }
    return bioKey
  }

  /**
   * Handles the response when the user clicks the edit profile button
   */
  editButtonClick(): void {
    if (this.editMode === false) this.socialService.setPreEditValues(this.socialMeta)
    else this.socialMeta = this.socialService.getPreEditValues()
    this.editMode = !this.editMode
    this.modifiedAnItem = false
    // populate child
    this.propertiesChild.setSitesFromBlogId(this.deriveActiveProperties())
  }

  /**
   * Handles the response when the user clicks the confirm edit button
   */
  editConfirmClick(): void {
    this.socialService.commitEditValues(this.socialMeta, this.activeUser)
    this.editMode = !this.editMode
    this.modifiedAnItem = false
    // clear empty values (now that they've been updated as empty on db)
    {
        const siteBioKeys = Object.keys(this.socialMeta)
        siteBioKeys
        .filter(aKey => this.siteSpecificBioFormat.test(aKey))
        .forEach(aKey => {
            if (this.socialMeta[aKey] !== '') return
            delete this.socialMeta[aKey] // for this page
            delete this.activeUser['social']['swarm_' + aKey] // across pages
            // force properties.component to change active properties
            this.propertiesChild.setSitesFromBlogId(this.deriveActiveProperties())
        })
    }
  }

  /**
   * Set that the profile has been modified during an edit, 
   * and that the results can be svaed
   */
  setModifiedAnItem(): void {
    this.modifiedAnItem = true
  }

  /**
   * Test values
   */
  testTemplateValue(val): string {
    return JSON.stringify(val)
  }

  /**
   * Derive the blog_id from the pattern bio_{blog_id} 
   * to get a list of active properties given a list of bios
   * @returns an array of blog_id
   */
  deriveActiveProperties() {
    // get the things starting with bio_
    const siteSpecificBioPattern = "bio_"
    const derivedSiteIds = Object.keys(this.socialMeta)
                                .filter(aKey => aKey.search(siteSpecificBioPattern) >= 0 && this.socialMeta[aKey].length > 0)
                                .map(i => parseInt(i.replace('bio_', ''))) // strip prefix to get Site blog_id
    return derivedSiteIds
  }


  /**
   * Handler for when the Properties child component adds/removes a property
   */
  handlePropertyChange($event) {
      const { property, action } = $event
      const keyName = `bio_${property.blog_id}`
      if (action === 'add') {
            if (keyName in this.socialMeta === false) this.socialMeta[keyName] = ""
            this.socialMetaMap[keyName] = property
      } else if (action === 'remove') {
            this.socialMeta[keyName] = ""
            delete this.socialMetaMap[keyName]
            this.modifiedAnItem = true
      }
  }

  ngAfterViewInit(): void {
    this.route.paramMap.subscribe((params:ParamMap) => {
        const idAsInt = parseInt(params.get('id'), 10)
        const initRoutine = () => {
            this.setActiveUser(idAsInt)
            this.setUserSocialMeta(this.activeUser)
            this.canEdit = this.userCanEdit()
        }
        //  get user list
        this.tryToSetUserList()
        if (this.users.length > 0) initRoutine()
        else this.userService.usersAdded.subscribe(() => {
                this.tryToSetUserList()
                initRoutine()
            });
    })
  }

}
